import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HTMLContentEditor } from '@/components/ui/html-content-editor';
import { ImageUpload } from '@/components/ui/image-upload';
import { AIArticleBuilder } from '@/components/article/AIArticleBuilder';
import { FeaturedImageSelector } from '@/components/article/FeaturedImageSelector';
import { ArticleFormData, Category, Tag, Author } from '@/types/article';
import { Badge } from '@/components/ui/badge';
import { CategorySelector } from '@/components/article/CategorySelector';
import { TagManager } from '@/components/article/TagManager';
import { AuthorSelector } from '@/components/article/AuthorSelector';
import { ArticlePreviewModal } from '@/components/article/ArticlePreviewModal';
import { ArticleFormErrorBoundary } from '@/components/article/ArticleFormErrorBoundary';
import { LinksTabErrorBoundary } from '@/components/article/LinksTabErrorBoundary';
import { useArticleAutoSave } from '@/hooks/useArticleAutoSave';
import { useArticleDraftAutoSave } from '@/hooks/useArticleDraftAutoSave';
import { useAutoSavePreference } from '@/hooks/useAutoSavePreference';
import { ArticleRevisionHistory } from '@/components/article/ArticleRevisionHistory';
import { ArticleScheduler } from '@/components/article/ArticleScheduler';
import { AutoSaveSettings } from '@/components/article/AutoSaveSettings';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { X, Clock, Eye, Save, Loader2, CloudUpload, Info } from 'lucide-react';
import { toast } from 'sonner';

// CRITICAL FIX: Remove lazy loading to prevent unmount/remount crashes
import { EnhancedLinksPreview } from '@/components/article/EnhancedLinksPreview';
import { KeywordQualityValidator } from '@/components/article/KeywordQualityValidator';
import { SEOAnalyzer } from '@/components/article/SEOAnalyzer';

const articleSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  content: z.string().min(1, 'Content is required'),
  excerpt: z.string().max(2500, 'Excerpt must be less than 2500 characters (approximately 300 words)').optional(),
  featured_image_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  featured_image_alt: z.string().max(100, 'Alt text must be less than 100 characters').optional(),
  meta_description: z.string().max(160, 'Meta description must be less than 160 characters').optional(),
  focus_keyword: z.string().max(50, 'Focus keyword must be less than 50 characters').optional(),
  canonical_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  og_title: z.string().max(60, 'OG title must be less than 60 characters').optional(),
  og_description: z.string().max(160, 'OG description must be less than 160 characters').optional(),
  og_image_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  category_id: z.string().optional(),
  author_id: z.string().min(1, 'Author is required when publishing').optional(),
  status: z.enum(['draft', 'published', 'scheduled', 'archived']),
  scheduled_at: z.string().optional(),
  timezone: z.string().optional(),
  tag_ids: z.array(z.string()).optional()
}).refine((data) => {
  // If status is published, author_id must be provided
  if (data.status === 'published' && (!data.author_id || data.author_id.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: 'Author is required for published articles',
  path: ['author_id']
}).refine((data) => {
  // If status is scheduled, scheduled_at must be provided and in the future
  if (data.status === 'scheduled') {
    if (!data.scheduled_at) {
      return false;
    }
    const scheduledDate = new Date(data.scheduled_at);
    const now = new Date();
    if (scheduledDate <= now) {
      return false;
    }
  }
  return true;
}, {
  message: 'Scheduled articles must have a future publication date',
  path: ['scheduled_at']
});

interface ArticleFormProps {
  seasonalTopic?: {
    title: string;
    description: string;
    keywords: string[];
  } | null;
  initialData?: Partial<ArticleFormData>;
  categories: Category[];
  tags: Tag[];
  authors: Author[];
  onSubmit: (data: ArticleFormData) => Promise<any>;
  onCancel: () => void;
  isLoading?: boolean;
  onCategoriesUpdate?: () => void;
  onTagsUpdate?: () => void;
  onAuthorsUpdate?: () => void;
}

export const ArticleForm: React.FC<ArticleFormProps> = ({
  seasonalTopic,
  initialData,
  categories,
  tags,
  authors,
  onSubmit,
  onCancel,
  isLoading = false,
  onCategoriesUpdate,
  onTagsUpdate,
  onAuthorsUpdate
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(initialData?.tag_ids || []);
  const [localCategories, setLocalCategories] = useState<Category[]>(categories);
  const [localTags, setLocalTags] = useState<Tag[]>(tags);
  const [localAuthors, setLocalAuthors] = useState<Author[]>(authors);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftArticleId, setDraftArticleId] = useState<string | undefined>(
    initialData && 'id' in initialData ? (initialData as any).id : undefined
  );

  // Get user's auto-save preference
  const { interval: autoSaveInterval } = useAutoSavePreference();
  
  const form = useForm<ArticleFormData>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      title: initialData?.title || '',
      content: initialData?.content || '',
      excerpt: initialData?.excerpt || '',
      featured_image_url: initialData?.featured_image_url || '',
      featured_image_alt: initialData?.featured_image_alt || '',
      meta_description: initialData?.meta_description || '',
      focus_keyword: initialData?.focus_keyword || '',
      canonical_url: initialData?.canonical_url || '',
      og_title: initialData?.og_title || '',
      og_description: initialData?.og_description || '',
      og_image_url: initialData?.og_image_url || '',
      category_id: initialData?.category_id || '',
      author_id: initialData?.author_id || '',
      status: initialData?.status || 'draft',
      scheduled_at: initialData?.scheduled_at || '',
      timezone: initialData?.timezone || 'UTC',
      tag_ids: initialData?.tag_ids || []
    }
  });

  // Sync selectedTags to form on mount and when initialData changes
  useEffect(() => {
    if (initialData?.tag_ids) {
      setSelectedTags(initialData.tag_ids);
    }
  }, [initialData]);

  // Reset form when initialData changes (for editing existing articles)
  useEffect(() => {
    if (initialData) {
      const tagIds = initialData.tag_ids || [];
      
      console.log('📝 Resetting form with initialData:', {
        title: initialData.title,
        tag_ids: tagIds,
        scheduled_at: initialData.scheduled_at,
        status: initialData.status
      });
      
      form.reset({
        title: initialData.title || '',
        content: initialData.content || '',
        excerpt: initialData.excerpt || '',
        featured_image_url: initialData.featured_image_url || '',
        featured_image_alt: initialData.featured_image_alt || '',
        meta_description: initialData.meta_description || '',
        focus_keyword: initialData.focus_keyword || '',
        canonical_url: initialData.canonical_url || '',
        og_title: initialData.og_title || '',
        og_description: initialData.og_description || '',
        og_image_url: initialData.og_image_url || '',
        category_id: initialData.category_id || '',
        author_id: initialData.author_id || '',
        status: initialData.status || 'draft',
        scheduled_at: initialData.scheduled_at || '',
        timezone: initialData.timezone || 'UTC',
        tag_ids: tagIds
      });
      setSelectedTags(tagIds);
      
      console.log('✅ Form reset complete, selectedTags set to:', tagIds);
    }
  }, [initialData, form]);

  // Auto-save functionality (sessionStorage)
  const { saveDraft, loadDraft, clearDraft } = useArticleAutoSave({
    getValues: form.getValues,
    articleId: draftArticleId,
    enabled: !isLoading
  });

  // Database auto-save functionality
  const { 
    saveDraft: saveDraftToDb, 
    lastSaveTime: dbLastSaveTime, 
    isSaving: isDbSaving,
    currentDraftId 
  } = useArticleDraftAutoSave({
    getValues: form.getValues,
    articleId: draftArticleId,
    enabled: !isLoading && !isGenerating,
    interval: autoSaveInterval, // Use user's preferred interval
    selectedTags, // CRITICAL FIX: Pass selectedTags to auto-save hook
    onDraftCreated: (newDraftId) => {
      setDraftArticleId(newDraftId);
      toast.info('Draft saved to database', {
        description: 'Your article is now accessible from the All Articles tab'
      });
    }
  });

  // Load draft on mount for new articles
  useEffect(() => {
    if (!initialData) {
      const draft = loadDraft();
      if (draft) {
        const shouldRestore = window.confirm(
          'We found an auto-saved draft. Would you like to restore it?'
        );
        if (shouldRestore) {
          Object.keys(draft).forEach((key) => {
            const value = draft[key as keyof ArticleFormData];
            if (value !== undefined) {
              form.setValue(key as keyof ArticleFormData, value);
            }
          });
          toast.success('Draft restored successfully');
        } else {
          clearDraft();
        }
      }
    }
  }, []);

  // Auto-save interval (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (saveDraft()) {
        setLastAutoSave(new Date());
      }
    }, 30000);

    const handleBeforeUnload = () => {
      saveDraft();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveDraft]);

  // Keyboard shortcut for manual save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save dialog
        
        // Trigger manual save
        saveDraftToDb();
        
        toast.success('Draft saved', {
          description: 'Keyboard shortcut: ' + (navigator.platform.includes('Mac') ? '⌘+S' : 'Ctrl+S')
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [saveDraftToDb]);

  // Consolidated form watching
  const formValues = form.watch();
  const watchedTitle = formValues.title;
  const watchedContent = formValues.content;
  const watchedStatus = formValues.status;

  const [hasAutoGenerated, setHasAutoGenerated] = useState({
    ogTitle: false,
    metaDescription: false,
    excerpt: false
  });
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);


  // Debounced auto-generation of SEO fields
  useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(() => {
      if (watchedTitle && !hasAutoGenerated.ogTitle && !form.getValues('og_title')) {
        form.setValue('og_title', watchedTitle.slice(0, 60));
        setHasAutoGenerated(prev => ({ ...prev, ogTitle: true }));
      }

      if (watchedContent && !hasAutoGenerated.metaDescription && !form.getValues('meta_description')) {
        const plainText = watchedContent.replace(/<[^>]*>/g, '');
        const excerpt = plainText.slice(0, 160);
        form.setValue('meta_description', excerpt);
        setHasAutoGenerated(prev => ({ ...prev, metaDescription: true }));
        
        if (!form.getValues('excerpt')) {
          form.setValue('excerpt', plainText.slice(0, 300));
          setHasAutoGenerated(prev => ({ ...prev, excerpt: true }));
        }
      }
    }, 300);

    setDebounceTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [watchedTitle, watchedContent]);

  const handlePublishNow = async () => {
    try {
      const currentValues = form.getValues();
      const existingDraftId = draftArticleId || currentDraftId;
      
      console.log('🏷️ Publishing with selectedTags:', selectedTags);
      console.log('🆔 Draft article ID:', existingDraftId);
      
      const publishData: any = {
        ...currentValues,
        status: 'published' as const,
        tag_ids: selectedTags,
        scheduled_at: currentValues.scheduled_at?.trim() ? currentValues.scheduled_at : null,
        published_at: new Date().toISOString()
      };
      
      if (existingDraftId) {
        publishData.id = existingDraftId;
      }
      
      // Validate required fields
      if (!publishData.author_id || publishData.author_id.trim() === '') {
        toast.error('Please select an author before publishing');
        setActiveTab('settings');
        return;
      }
      if (!publishData.title || publishData.title.trim() === '') {
        toast.error('Please add a title before publishing');
        setActiveTab('content');
        return;
      }
      if (!publishData.content || publishData.content.trim() === '') {
        toast.error('Please add content before publishing');
        setActiveTab('content');
        return;
      }
      
      const result = await onSubmit(publishData);
      console.log('✅ Article published successfully');
    } catch (error: any) {
      console.error('❌ Publish error:', error);
      
      // CRITICAL FIX: Show specific error messages - handle Supabase error objects
      const errorMessage = error?.message || error?.error?.message || (typeof error === 'string' ? error : JSON.stringify(error));
      
      if (errorMessage.includes('row-level security') || errorMessage.includes('RLS')) {
        toast.error('Permission denied. Please ensure you are logged in and have permission to publish.');
      } else if (errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint')) {
        toast.error('An article with this title/slug already exists. Please try a different title.');
      } else if (errorMessage.includes('article_tags')) {
        toast.error('Failed to save article tags. Please try again.');
      } else {
        toast.error(`Failed to publish: ${errorMessage}`);
      }
    }
  };

  const handleSaveDraft = async () => {
    try {
      const currentValues = form.getValues();
      const existingDraftId = draftArticleId || currentDraftId;
      
      const draftData: any = {
        ...currentValues,
        status: 'draft' as const,
        tag_ids: selectedTags
      };
      
      if (existingDraftId) {
        draftData.id = existingDraftId;
      }
      
      console.log('💾 Saving draft:', { 
        title: draftData.title, 
        id: draftData.id,
        hasDraftId: !!existingDraftId 
      });
      
      await handleSubmit(draftData);
      // Note: Navigation is handled by handleSubmit -> onSubmit in parent
    } catch (error: any) {
      console.error('❌ Save draft error:', error);
      
      // CRITICAL FIX: Show specific error and DON'T navigate on failure - handle Supabase error objects
      const errorMessage = error?.message || error?.error?.message || (typeof error === 'string' ? error : JSON.stringify(error));
      
      if (errorMessage.includes('row-level security') || errorMessage.includes('RLS')) {
        toast.error('Permission denied. Please ensure you are logged in.');
      } else if (errorMessage.includes('duplicate key')) {
        toast.error('A draft with this title already exists.');
      } else {
        toast.error(`Failed to save draft: ${errorMessage}`);
      }
      // Don't navigate - keep user on page with their content
    }
  };

  const handleSubmit = async (data: ArticleFormData) => {
    try {
      console.log('📤 Submitting article...', { title: data.title, status: data.status, draftId: currentDraftId });
      
      // Auto-set status to 'scheduled' if scheduled_at is provided and in the future
      if (data.scheduled_at) {
        const scheduledDate = new Date(data.scheduled_at);
        const now = new Date();
        if (scheduledDate > now) {
          data.status = 'scheduled';
          console.log('✅ Auto-setting status to scheduled due to future scheduled_at date');
        }
      }
      
      // Additional validation for published articles
      if (data.status === 'published') {
        if (!data.author_id || data.author_id.trim() === '') {
          toast.error('Please select an author before publishing');
          return;
        }
        if (!data.title || data.title.trim() === '') {
          toast.error('Please add a title before publishing');
          return;
        }
        if (!data.content || data.content.trim() === '') {
          toast.error('Please add content before publishing');
          return;
        }
      }
      
      // Validation for scheduled articles
      if (data.status === 'scheduled') {
        if (!data.author_id || data.author_id.trim() === '') {
          toast.error('Please select an author before scheduling');
          return;
        }
        if (!data.scheduled_at) {
          toast.error('Please set a publication date for scheduled articles');
          return;
        }
      }

      // Auto-generate canonical URL if not set
      if (!data.canonical_url && data.title) {
        const slug = data.title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .slice(0, 100);
        data.canonical_url = `https://storily.io/articles/${slug}`;
      }

      // Auto-set OG image to featured image if not explicitly set
      if (!data.og_image_url && data.featured_image_url) {
        data.og_image_url = data.featured_image_url;
      }

      // Auto-set OG description to meta description if not set
      if (!data.og_description && data.meta_description) {
        data.og_description = data.meta_description;
      }

      console.log('🏷️ About to submit with selectedTags:', selectedTags);
      console.log('🏷️ Tag count:', selectedTags.length);
      
      // Include draft ID if it exists so parent knows to update instead of create
      const result = await onSubmit({
        ...data,
        tag_ids: selectedTags,
        ...(currentDraftId && { id: currentDraftId })
      } as any);
      
      console.log('✅ Article submitted successfully', result);
      
      // CRITICAL FIX: Capture the article ID after first save so subsequent saves UPDATE instead of CREATE
      if (result?.id && !currentDraftId) {
        setDraftArticleId(result.id);
        console.log('📝 Captured new article ID for future updates:', result.id);
      }
      
      // Clear auto-saved draft on successful submit
      clearDraft();
    } catch (error) {
      console.error('❌ Article submission error:', error);
      toast.error('Failed to save article. Please check the console for details.');
      throw error; // Re-throw to ensure error handling works
    }
  };

  const toggleTag = (tagId: string) => {
    console.log('🏷️ toggleTag called with:', tagId);
    setSelectedTags(prev => {
      const newTags = prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId];
      console.log('🏷️ Updated selectedTags to:', newTags);
      return newTags;
    });
    // Note: Form sync happens via useEffect watching selectedTags
  };

  const getSelectedTagNames = () => {
    return localTags.filter(tag => selectedTags.includes(tag.id));
  };

  // Calculate reading time based on content (memoized)
  const currentReadingTime = useMemo(() => {
    if (!watchedContent) return 0;
    const plainText = watchedContent.replace(/<[^>]*>/g, '');
    const words = plainText.split(/\s+/).length;
    return Math.ceil(words / 200); // Assuming 200 words per minute
  }, [watchedContent]);

  // Extract external sources from content (memoized)
  const externalSources = useMemo(() => {
    if (!watchedContent) return [];
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(watchedContent, 'text/html');
      
      // Find all external links (http/https links that are not internal blog links)
      const allLinks = doc.querySelectorAll('a[href]');
      const externalLinks = Array.from(allLinks)
        .map(link => {
          const href = link.getAttribute('href') || '';
          const title = link.textContent?.trim() || href;
          return { title, url: href };
        })
        .filter(source => {
          // Only include http/https links that are not internal /blog/ links
          return source.url && 
                 (source.url.startsWith('http://') || source.url.startsWith('https://')) &&
                 !source.url.includes('/blog/');
        });
      
      // Remove duplicates based on URL
      const uniqueSources = Array.from(
        new Map(externalLinks.map(item => [item.url, item])).values()
      );
      
      return uniqueSources;
    } catch (error) {
      console.error('Error extracting external sources:', error);
      return [];
    }
  }, [watchedContent]);

  // Update local state when props change
  useEffect(() => {
    setLocalCategories(categories);
    setLocalTags(tags);
    setLocalAuthors(authors);
  }, [categories, tags, authors]);

  // CRITICAL FIX: Sync selectedTags to form field whenever it changes
  useEffect(() => {
    form.setValue('tag_ids', selectedTags);
    console.log('🔄 Synced selectedTags to form:', selectedTags);
  }, [selectedTags, form]);

  const handleCategoriesUpdate = () => {
    if (onCategoriesUpdate) {
      onCategoriesUpdate();
    }
  };

  const handleTagsUpdate = () => {
    if (onTagsUpdate) {
      onTagsUpdate();
    }
  };

  const handleAuthorsUpdate = async () => {
    if (onAuthorsUpdate) {
      await onAuthorsUpdate();
    }
  };

  const handleAIArticleGenerated = async (data: {
    title: string;
    content: string;
    excerpt?: string;
    meta_description?: string;
    focus_keyword?: string;
    og_title?: string;
    og_description?: string;
  }) => {
    console.log('AI Article Generated:', data);
    setIsGenerating(false);
    
    // Just load content into form, don't submit
    form.setValue('title', data.title);
    form.setValue('content', data.content);
    if (data.excerpt) form.setValue('excerpt', data.excerpt);
    if (data.meta_description) form.setValue('meta_description', data.meta_description);
    if (data.focus_keyword) form.setValue('focus_keyword', data.focus_keyword);
    if (data.og_title) form.setValue('og_title', data.og_title);
    if (data.og_description) form.setValue('og_description', data.og_description);
    
    // Auto-select first author if none selected
    const currentAuthor = form.getValues('author_id');
    if (!currentAuthor && localAuthors.length > 0) {
      form.setValue('author_id', localAuthors[0].id);
    }
    
    setActiveTab('content');
    toast.success('Article loaded! You can now review and publish.');
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleRestoreRevision = (content: string, title: string) => {
    form.setValue('content', content);
    form.setValue('title', title);
    toast.success('Revision restored', {
      description: 'The content and title have been restored. Remember to save your changes.'
    });
  };

  return (
    <ArticleFormErrorBoundary fallbackMessage="An error occurred in the article form. Please refresh the page to try again.">
      <div className="space-y-6">
        {/* Auto-save indicator */}
        {lastAutoSave && (
          <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
            <Save className="w-3 h-3" />
            <span>Auto-saved at {lastAutoSave.toLocaleTimeString()}</span>
          </div>
        )}

        {/* AI Article Builder - show for new articles or when seasonal topic is provided */}
        {(!initialData || seasonalTopic) && (
          <AIArticleBuilder 
            initialTopic={seasonalTopic || undefined}
            onArticleGenerated={handleAIArticleGenerated}
            onGeneratingChange={setIsGenerating}
          />
        )}
        
        <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Tabs defaultValue="content" value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="seo" disabled={isGenerating}>
              SEO & Metadata {isGenerating && <span className="ml-1 text-xs">(Generating...)</span>}
            </TabsTrigger>
            <TabsTrigger value="settings" disabled={isGenerating}>
              Settings {isGenerating && <span className="ml-1 text-xs">(Generating...)</span>}
            </TabsTrigger>
            <TabsTrigger value="history" disabled={!draftArticleId}>
              Revision History
            </TabsTrigger>
            <TabsTrigger value="preview" disabled={isGenerating}>
              SEO Preview {isGenerating && <span className="ml-1 text-xs">(Generating...)</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <Alert className="flex-1 bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  {autoSaveInterval === 0 
                    ? 'Auto-save is disabled. Use the "Save Draft" button or press Ctrl+S (⌘+S on Mac) to save manually.'
                    : `Your article is automatically saved every ${autoSaveInterval / 1000} seconds. You can also press Ctrl+S (⌘+S on Mac) to save immediately.`
                  }
                </AlertDescription>
              </Alert>
              <AutoSaveSettings />
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Article Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter article title..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="excerpt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Excerpt (approximately 300 words)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Brief summary of the article that includes the focus keyword..."
                          rows={8}
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {field.value ? `${field.value.length} characters / ~${Math.round(field.value.split(/\s+/).filter(w => w.length > 0).length)} words` : '0 characters / 0 words'}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content</FormLabel>
                      <FormControl>
                        <HTMLContentEditor
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Write your article content here..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <FeaturedImageSelector
                    currentImageUrl={formValues.featured_image_url || ''}
                    onImageSelected={(url) => form.setValue('featured_image_url', url)}
                    onAltTextGenerated={(altText) => form.setValue('featured_image_alt', altText)}
                    articleTitle={watchedTitle || ''}
                    articleExcerpt={formValues.excerpt || ''}
                    keywords={formValues.focus_keyword || ''}
                  />
                  
                  <FormField
                    control={form.control}
                    name="featured_image_alt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image Alt Text</FormLabel>
                        <FormControl>
                          <Input placeholder="Describe the image..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="seo" className="space-y-6">
            <KeywordQualityValidator
              focusKeyword={formValues.focus_keyword || ''}
              articleTitle={watchedTitle || ''}
              currentArticleSlug={initialData && 'slug' in initialData ? (initialData as any).slug : undefined}
            />
            
            <Card>
              <CardHeader>
                <CardTitle>SEO Optimization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="focus_keyword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Focus Keyword</FormLabel>
                      <FormControl>
                        <Input placeholder="Main keyword for SEO..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="meta_description"
                  render={({ field }) => {
                    const length = field.value?.length || 0;
                    const isOverLimit = length > 160;
                    return (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Description for search engines..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <p className={`text-xs mt-1 ${isOverLimit ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                          {length} / 160 characters {isOverLimit && '(Too long! Search engines may truncate this.)'}
                        </p>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name="canonical_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Canonical URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="og_title"
                    render={({ field }) => {
                      const length = field.value?.length || 0;
                      const isOverLimit = length > 60;
                      return (
                        <FormItem>
                          <FormLabel>Open Graph Title</FormLabel>
                          <FormControl>
                            <Input placeholder="Social media title..." {...field} />
                          </FormControl>
                          <p className={`text-xs mt-1 ${isOverLimit ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                            {length} / 60 characters {isOverLimit && '(Too long!)'}
                          </p>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="og_image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Open Graph Image URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="og_description"
                  render={({ field }) => {
                    const length = field.value?.length || 0;
                    const isOverLimit = length > 160;
                    return (
                      <FormItem>
                        <FormLabel>Open Graph Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Social media description..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <p className={`text-xs mt-1 ${isOverLimit ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                          {length} / 160 characters {isOverLimit && '(Too long!)'}
                        </p>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Article Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Reading Time Display */}
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Estimated reading time: <strong>{currentReadingTime} minute{currentReadingTime !== 1 ? 's' : ''}</strong>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <CategorySelector
                            categories={localCategories}
                            value={field.value}
                            onValueChange={field.onChange}
                            onCategoriesUpdate={handleCategoriesUpdate}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="author_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Author</FormLabel>
                        <FormControl>
                          <AuthorSelector
                            authors={localAuthors}
                            value={field.value}
                            onValueChange={field.onChange}
                            onAuthorsUpdate={handleAuthorsUpdate}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Publish Now</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Scheduled Publishing */}
                {(watchedStatus === 'draft' || watchedStatus === 'scheduled') && (
                  <ArticleScheduler
                    scheduledAt={form.watch('scheduled_at') || ''}
                    onScheduledAtChange={(value) => form.setValue('scheduled_at', value)}
                    timezone={form.watch('timezone') || 'UTC'}
                    onTimezoneChange={(value) => form.setValue('timezone', value)}
                  />
                )}

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FormLabel>Tags</FormLabel>
                    {selectedTags.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected
                      </Badge>
                    )}
                  </div>
                  <TagManager
                    tags={localTags}
                    selectedTags={selectedTags}
                    onTagToggle={toggleTag}
                    onTagsUpdate={handleTagsUpdate}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <ArticleRevisionHistory
              articleId={draftArticleId}
              onRestoreRevision={handleRestoreRevision}
            />
          </TabsContent>

          <TabsContent value="preview" className="space-y-6">
            <SEOAnalyzer
              title={watchedTitle || ''}
              content={watchedContent || ''}
              metaDescription={formValues.meta_description || ''}
              focusKeyword={formValues.focus_keyword || ''}
              excerpt={formValues.excerpt || ''}
              featuredImageUrl={formValues.featured_image_url || ''}
              featuredImageAlt={formValues.featured_image_alt || ''}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center">
          {/* Auto-save indicator */}
          <div className="flex items-center gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsPreviewOpen(true)}
              className="gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview Article
            </Button>
            
            {dbLastSaveTime && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {isDbSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving draft...</span>
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-4 h-4 text-green-600" />
                    <span>Last saved: {dbLastSaveTime.toLocaleTimeString()}</span>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div className="flex space-x-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleSaveDraft}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Draft
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              type="button"
              disabled={isLoading} 
              className="gap-2"
              onClick={handlePublishNow}
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading 
                ? 'Publishing...' 
                : 'Publish Now'
              }
            </Button>
          </div>
        </div>

        {/* Article Preview Modal */}
        <ArticlePreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          title={watchedTitle || ''}
          content={watchedContent || ''}
          excerpt={formValues.excerpt || ''}
          featuredImageUrl={formValues.featured_image_url || ''}
          featuredImageAlt={formValues.featured_image_alt || ''}
          category={localCategories.find(cat => cat.id === formValues.category_id)}
          author={localAuthors.find(auth => auth.id === formValues.author_id)}
          tags={localTags.filter(tag => selectedTags.includes(tag.id))}
          readingTime={currentReadingTime}
        />
        </form>
      </Form>
    </div>
  </ArticleFormErrorBoundary>
  );
};